'use client';

/**
 * CollaboratorManagePanel
 *
 * Client island that replaces the static collaborator list on the collection
 * detail page for owners and admins. It fetches the live collaborator list,
 * allows role editing, removal, and embedding the CollaboratorInviteForm.
 *
 * Layout:
 *   ┌───────────────────────────────────────┐
 *   │ Collaborators (count)          [+ Invite] │
 *   ├───────────────────────────────────────┤
 *   │ [Avatar] displayName  [role ▾]  [Remove] │
 *   │ …                                        │
 *   ├───────────────────────────────────────┤
 *   │ CollaboratorInviteForm (inline)           │
 *   └───────────────────────────────────────┘
 *
 * Props:
 *   collectionId   — the collection being managed
 *   isOwner        — whether the current user is the owner (vs admin collab)
 *   currentUserId  — MongoDB userId of the authenticated viewer
 *   initialCollaborators — SSR-rendered snapshot so no loading flash
 */

import Link from 'next/link';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  fetchCollaborators,
  updateCollaboratorRole,
  removeCollaborator,
} from '@/lib/api';
import { UserInviteSearch } from '@/components/users/user-invite-search';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface Collaborator {
  userId?: string;
  email?: string;
  role: string;
  status: string;
  user?: {
    _id: string;
    displayName: string;
    username: string;
    imageUrl?: string;
  } | null;
}

interface CollaboratorManagePanelProps {
  collectionId: string;
  /** True for both owners and admin collaborators — gates role editing and removal */
  canManage: boolean;
  currentUserId: string;
  initialCollaborators: Collaborator[];
}

export function CollaboratorManagePanel({
  collectionId,
  canManage,
  currentUserId,
  initialCollaborators,
}: CollaboratorManagePanelProps) {
  const queryClient = useQueryClient();

  // Use SSR data as the initial cache entry so there's no loading flash
  const { data } = useQuery({
    queryKey: ['collaborators', collectionId],
    queryFn: () => fetchCollaborators(collectionId),
    initialData: { collaborators: initialCollaborators },
  });

  const collaborators: Collaborator[] = data?.collaborators ?? [];
  const existingCollaboratorIds = new Set(
    collaborators.map((c) => c.userId).filter(Boolean) as string[]
  );

  // ── role update mutation ────────────────────────────────────────────────────
  const { mutate: changeRole, isPending: isChangingRole } = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      updateCollaboratorRole(collectionId, userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collaborators', collectionId] });
      toast.success('Role updated');
    },
    onError: (err: Error) => toast.error(err.message ?? 'Failed to update role'),
  });

  // ── remove mutation ─────────────────────────────────────────────────────────
  const { mutate: remove, isPending: isRemoving } = useMutation({
    mutationFn: (userId: string) => removeCollaborator(collectionId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collaborators', collectionId] });
      toast.success('Collaborator removed');
    },
    onError: (err: Error) => toast.error(err.message ?? 'Failed to remove collaborator'),
  });

  const isBusy = isChangingRole || isRemoving;

  return (
    <div className="space-y-4">
      {/* Collaborator rows */}
      {collaborators.length === 0 ? (
        <p className="text-sm text-muted-foreground">No collaborators yet.</p>
      ) : (
        <ul className="divide-y border rounded-lg">
          {collaborators.map((c, i) => {
            const identifier = c.userId ?? c.email ?? String(i);
            const displayName = c.user?.displayName ?? c.email ?? 'Unknown';
            const avatarUrl = c.user?.imageUrl;
            const initials = displayName[0].toUpperCase();

            // Can modify anyone except yourself (owners/admins can't self-demote here)
            const canModify = canManage && c.userId !== currentUserId;

            return (
              <li key={identifier} className="flex items-center gap-3 px-4 py-3">
                <Avatar className="h-7 w-7 shrink-0">
                  <AvatarImage src={avatarUrl} />
                  <AvatarFallback>{initials}</AvatarFallback>
                </Avatar>

                <div className="flex-1 min-w-0">
                  {c.user?._id ? (
                    <Link
                      href={`/u/${c.user._id}`}
                      className="text-sm font-medium truncate hover:underline underline-offset-2 decoration-muted-foreground/40"
                    >
                      {displayName}
                    </Link>
                  ) : (
                    <p className="text-sm font-medium truncate">{displayName}</p>
                  )}
                  {c.user?.username && (
                    <p className="text-xs text-muted-foreground">@{c.user.username}</p>
                  )}
                </div>

                {/* Status badge — only shown when not yet accepted */}
                {c.status !== 'accepted' && (
                  <Badge variant="secondary" className="text-xs shrink-0">
                    {c.status}
                  </Badge>
                )}

                {/* Role picker — owner-only edit */}
                {canModify && c.userId ? (
                  <Select
                    value={c.role}
                    onValueChange={(role) => changeRole({ userId: c.userId!, role })}
                    disabled={isBusy}
                  >
                    <SelectTrigger className="w-24 h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="viewer">Viewer</SelectItem>
                      <SelectItem value="editor">Editor</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge variant="outline" className="text-xs shrink-0">
                    {c.role}
                  </Badge>
                )}

                {/* Remove button — owner can remove anyone except self */}
                {canModify && c.userId && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive shrink-0"
                        disabled={isBusy}
                        aria-label={`Remove ${displayName}`}
                      >
                        Remove
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remove collaborator?</AlertDialogTitle>
                        <AlertDialogDescription>
                          {displayName} will lose access to this collection immediately. You can
                          re-invite them at any time.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          onClick={() => remove(c.userId!)}
                        >
                          Remove
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Invite collaborators — user search with inline role picker */}
      <UserInviteSearch
        mode="collaborator"
        collectionId={collectionId}
        existingIds={existingCollaboratorIds}
        onAdded={() => {
          queryClient.invalidateQueries({ queryKey: ['collaborators', collectionId] });
        }}
      />
    </div>
  );
}
